import { motion } from 'framer-motion';
import React, { ReactNode } from 'react';

interface AnimatedCardProps {
  children: ReactNode;
  delay?: number;
  variant?: 'fadeIn' | 'slideUp' | 'slideLeft' | 'scale' | 'flip';
  onClick?: () => void;
  className?: string;
  style?: React.CSSProperties;
}

const variants = {
  fadeIn: {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
  },
  slideUp: {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0 },
  },
  slideLeft: {
    hidden: { opacity: 0, x: 50 },
    visible: { opacity: 1, x: 0 },
  },
  scale: {
    hidden: { opacity: 0, scale: 0.8 },
    visible: { opacity: 1, scale: 1 },
  },
  flip: {
    hidden: { opacity: 0, rotateY: 90 },
    visible: { opacity: 1, rotateY: 0 },
  },
};

export function AnimatedCard({
  children,
  delay = 0,
  variant = 'fadeIn',
  onClick,
  className,
  style,
}: AnimatedCardProps) {
  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={variants[variant]}
      transition={{
        duration: 0.5,
        delay,
        ease: 'easeOut',
      }}
      whileHover={{
        scale: onClick ? 1.05 : 1,
        transition: { duration: 0.2 },
      }}
      whileTap={onClick ? { scale: 0.95 } : {}}
      onClick={onClick}
      className={className}
      style={{
        ...style,
        cursor: onClick ? 'pointer' : 'default',
      }}
    >
      {children}
    </motion.div>
  );
}

// Specialized button component with Framer Motion
interface AnimatedButtonProps {
  children: ReactNode;
  onClick?: () => void;
  variant?: 'primary' | 'secondary' | 'success' | 'danger';
  disabled?: boolean;
  style?: React.CSSProperties;
}

export function AnimatedButton({
  children,
  onClick,
  variant = 'primary',
  disabled = false,
  style,
}: AnimatedButtonProps) {
  const colors = {
    primary: {
      bg: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      shadow: 'rgba(102, 126, 234, 0.4)',
    },
    secondary: {
      bg: 'linear-gradient(135deg, #64748b 0%, #475569 100%)',
      shadow: 'rgba(100, 116, 139, 0.4)',
    },
    success: {
      bg: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
      shadow: 'rgba(16, 185, 129, 0.4)',
    },
    danger: {
      bg: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
      shadow: 'rgba(239, 68, 68, 0.4)',
    },
  };

  return (
    <motion.button
      whileHover={!disabled ? { scale: 1.05, y: -2 } : {}}
      whileTap={!disabled ? { scale: 0.95 } : {}}
      transition={{ duration: 0.2 }}
      onClick={onClick}
      disabled={disabled}
      style={{
        background: colors[variant].bg,
        border: 'none',
        borderRadius: '12px',
        padding: '1rem 2rem',
        color: 'white',
        fontSize: '1rem',
        fontWeight: '600',
        cursor: disabled ? 'not-allowed' : 'pointer',
        boxShadow: `0 4px 15px ${colors[variant].shadow}`,
        opacity: disabled ? 0.6 : 1,
        ...style,
      }}
    >
      <motion.span
        initial={{ opacity: 0.9 }}
        animate={{ opacity: 1 }}
        transition={{ repeat: Infinity, duration: 2, repeatType: 'reverse' }}
      >
        {children}
      </motion.span>
    </motion.button>
  );
}

// Stagger children animation
interface AnimatedListProps {
  children: ReactNode;
  staggerDelay?: number;
}

export function AnimatedList({ children, staggerDelay = 0.1 }: AnimatedListProps) {
  const childrenArray = React.Children.toArray(children);

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={{
        visible: {
          transition: {
            staggerChildren: staggerDelay,
          },
        },
      }}
    >
      {childrenArray.map((child, index) => (
        <motion.div
          key={index}
          variants={{
            hidden: { opacity: 0, y: 20 },
            visible: { opacity: 1, y: 0 },
          }}
        >
          {child}
        </motion.div>
      ))}
    </motion.div>
  );
}
